import type { MonitoringFilters, MonitoringView } from '../hooks/useMonitoringFilters';
import { getMaintenanceGroupName } from './maintenanceGroupName';
import useFmText from './useFmText';
import './FilterChips.scss';

interface IFilterChipsProps {
    filters: MonitoringFilters;
    onChange: (patch: Partial<MonitoringFilters>) => void;
    onReset: () => void;
    onApplyView: (view: MonitoringView) => void;
    accountNameByKey: Map<string, string>;
}

const VIEWS: MonitoringView[] = ['delayed', 'warrantyWithoutService', 'withoutVolksTotal', 'pendingCampaigns'];

const FilterChips = ({ filters, onChange, onReset, onApplyView, accountNameByKey }: IFilterChipsProps) => {
    const { t, vehicleType } = useFmText();
    const chips: { label: string; clear: () => void }[] = [];

    if (filters.q) {
        chips.push({
            label: `${t(`search.field.${filters.qField}`)}: ${filters.q}`,
            clear: () => onChange({ q: '' }),
        });
    }
    if (filters.tile) {
        chips.push({ label: t(`tiles.${filters.tile}`), clear: () => onChange({ tile: null }) });
    }
    if (filters.types.length) {
        chips.push({
            label: `${t('filters.vehicleType')}: ${filters.types.map(vehicleType).join(', ')}`,
            clear: () => onChange({ types: [] }),
        });
    }
    if (filters.warranty) {
        chips.push({ label: t(`warranty.${filters.warranty}`), clear: () => onChange({ warranty: '' }) });
    }
    if (filters.withCampaign) {
        chips.push({ label: t('filters.pendingCampaign'), clear: () => onChange({ withCampaign: false }) });
    }
    if (filters.accounts.length) {
        chips.push({
            label:
                filters.accounts.length > 2
                    ? t('chips.accountsSelected', { count: filters.accounts.length })
                    : `${t('filters.account')}: ${filters.accounts.map(id => accountNameByKey.get(id) ?? id).join(', ')}`,
            clear: () => onChange({ accounts: [] }),
        });
    }
    if (filters.maintenanceGroups.length) {
        chips.push({
            label: `${t('filters.maintenanceGroup')}: ${filters.maintenanceGroups.map(value => getMaintenanceGroupName(t, value)).join(', ')}`,
            clear: () => onChange({ maintenanceGroups: [] }),
        });
    }

    return (
        <div className='fm-chipsrow'>
            {chips.map(chip => (
                <span className='fm-chip' key={chip.label}>
                    {chip.label}
                    <button type='button' onClick={chip.clear} aria-label={t('chips.remove')}>
                        ×
                    </button>
                </span>
            ))}
            {chips.length > 0 && (
                <>
                    <button type='button' className='fm-linkbtn' onClick={onReset}>
                        {t('chips.clearAll')}
                    </button>
                    <span className='fm-sep' />
                </>
            )}
            <span className='fm-views'>
                <span className='fm-views-label'>{t('views.label')}</span>
                {VIEWS.map(view => (
                    <button type='button' key={view} className='fm-view' onClick={() => onApplyView(view)}>
                        {t(`views.${view}`)}
                    </button>
                ))}
            </span>
        </div>
    );
};

export default FilterChips;
